import { useMemo } from "react";
import { Localized, useLocalization } from "@fluent/react";
import { featureEnabled } from "byzantine/src/Feature";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import { useInstitutionSettings } from "../contexts/InstitutionSettingsContext";
import AppAuthorize from "../AppAuthorize";
import PaymentsEntryCard from "./PaymentsEntryCard";
import {
  OptionCard,
  useMoneyMovementMethods,
  VALID_MOM_OPTIONS,
} from "../../src/modules/transfers";

import styles from "./CustomizableTransferMenu.module.scss";

const PAYMENTS_ENTRY_CARD_TYPES = new Set(["ach_payments", "wires"]);
const isValidMoneyMovementOption = (
  type: string
): type is (typeof VALID_MOM_OPTIONS)[number] => VALID_MOM_OPTIONS.includes(type as (typeof VALID_MOM_OPTIONS)[number]);

const CustomizableTransferMenu = () => {
  // the style rules change depending on if there are an odd or even number of options =(
  const features = useUserFeatures();
  const { l10n } = useLocalization();
  const institutionSettings = useInstitutionSettings();
  const moneyMovementFeaturesData = useMoneyMovementMethods();

  if (!moneyMovementFeaturesData) {
    return null;
  }

  const shouldShowPaymentsEntryCard = featureEnabled(features, {
    or: ["payment_activity"],
  });
  const paymentsEntryCardIndex = useMemo(() => {
    if (!shouldShowPaymentsEntryCard || !moneyMovementFeaturesData) return -1;
    return moneyMovementFeaturesData.findIndex(
      (feature) => feature?.type && PAYMENTS_ENTRY_CARD_TYPES.has(feature.type)
    );
  }, [moneyMovementFeaturesData, shouldShowPaymentsEntryCard]);

  return (
    <div className={styles.container}>
      <Localized id="heading-how-send-money">
        <h1 className={styles.header}>How do you want to send money?</h1>
      </Localized>

      <div className={styles["grid-container"]}>
        {moneyMovementFeaturesData?.map((feature, index) => {
          if (feature?.type === "naf") {
            return (
              <AppAuthorize
                key={feature?.nafData?.client_id}
                app={feature?.nafData}
                institution={institutionSettings}
                renderButton={(handleClick) => (
                  <OptionCard
                    icon={feature?.nafData?.image}
                    title={feature?.nafData?.name}
                    description={feature?.nafData?.description}
                    iconSize="24px"
                    onCardClick={handleClick}
                  />
                )}
              />
            );
          }

          if (feature?.type && isValidMoneyMovementOption(feature.type)) {
            if (
              PAYMENTS_ENTRY_CARD_TYPES.has(feature.type) &&
              shouldShowPaymentsEntryCard
            ) {
              if (index === paymentsEntryCardIndex) {
                return <PaymentsEntryCard key="payments-entry-card" />;
              }
              return null;
            }

            return (
              <OptionCard
                key={feature?.type}
                icon={feature?.data?.icon}
                iconSize={feature?.data?.iconSize}
                title={
                  typeof feature.data?.title === "string"
                    ? l10n.getString(
                        feature?.data?.translationTitle || "",
                        null,
                        feature?.data?.title
                      )
                    : feature?.data?.title
                }
                description={l10n.getString(
                  feature?.data?.translationDescription as string,
                  null,
                  feature?.data?.description
                )}
                route={feature?.data?.route}
                customOptionButtons={feature?.data?.customOptionButtons}
                shouldOpenInNewTab={
                  !!features?.disable_billpay_native_flow &&
                  feature?.type === "bill_pay"
                }
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default CustomizableTransferMenu;
